/**
 * Which columns a board draws.
 *
 * Shared by the two boards, because the rule is the same on both and has
 * nothing to do with what the rows are: every column can be hidden except the
 * one that identifies the row, and a board with that one hidden is a board with
 * nothing to click.
 *
 * **Hiding is not a request.** Which columns somebody wants to look at is about
 * this screen and nothing else — no server, and nothing about who the rows are.
 */

/** As little of a column as this needs, so either board's can be passed. */
export interface PickableColumn {
  readonly id: string
  readonly label: string
  /** Always drawn: the column a row is identified by. */
  readonly fixed: boolean
}

export function ColumnPicker({
  columns,
  hidden,
  onHidden,
}: {
  readonly columns: readonly PickableColumn[]
  readonly hidden: ReadonlySet<string>
  readonly onHidden: (hidden: ReadonlySet<string>) => void
}) {
  return (
    <>
      <p className="mb-2 font-semibold text-[12px] text-t2 uppercase tracking-[0.06em]">
        Display columns · {columns.length - hidden.size} shown
      </p>
      <span className="block max-h-64 overflow-y-auto">
        {columns.map((column) => (
          <button
            className="flex w-full items-center gap-2 rounded-field px-1.5 py-1.5 text-left text-[13px] text-t1 hover:bg-line-2 disabled:opacity-50"
            disabled={column.fixed}
            key={column.id}
            onClick={() => {
              const next = new Set(hidden)
              if (next.has(column.id)) next.delete(column.id)
              else next.add(column.id)
              onHidden(next)
            }}
            type="button"
          >
            {/*
              Decorative: the button is named by the column, and a tick read out
              as part of that name gives "✓ Asks for" — which is the state and
              the label run together into neither.
            */}
            <span
              aria-hidden="true"
              className={`flex h-3.5 w-3.5 flex-none items-center justify-center rounded-[3px] border text-[9px] ${
                hidden.has(column.id) ? 'border-line' : 'border-brand bg-brand text-brand-contrast'
              }`}
            >
              {hidden.has(column.id) ? '' : '✓'}
            </span>
            <span className="min-w-0 flex-1 truncate">{column.label}</span>
          </button>
        ))}
      </span>
    </>
  )
}
